import { inject, Injectable, signal } from '@angular/core';
import { AccountContext, AccountType, User } from '../models/user.model';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AlertService, AlertTypeEnum } from './alert.service';
import { StorageService } from './storage.service';
import { FieldError, ApiResponse } from '../models/api-response.model';
import { OneTimePassword } from '../models/one-time-password.model';
import { environment } from '../../environments/environment';
import { UserAdapter } from '../adapters/user.adapter';
import { Observable, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
  http = inject(HttpClient);
  storageService = inject(StorageService);
  alertService = inject(AlertService);
  router = inject(Router);
  userAdapter = inject(UserAdapter);

  specificErrorMessages = signal<FieldError[]>([]);
  errorMessage = signal<string | null>(null);
  oneTimePassword = signal<OneTimePassword | null>(null);

  currentUser = signal<User | null>(this.storageService.getItem('user'));
  loggingIn = signal(false);
  loggingOut = signal(false);
  loggedIn = signal(!!this.storageService.getItem('auth_token') && !!this.storageService.getItem('user'));
  token = signal<string | null>(this.storageService.getItem('auth_token'));
  otpEmail = signal<{ email: string; action: string; code?: string } | null>(null);

  login(data: { email: string; password: string }) {
    this.loggingIn.set(true);
    this.errorMessage.set(null);
    this.specificErrorMessages.set([]);
    this.http
      .post<ApiResponse>(`${environment.baseUrl}/auth/login`, data)
      .subscribe({
        next: async (response: any) => {
          this.loggingIn.set(false);
          if (!response.success) {
            let errors = response.data;
            this.errorMessage.set(response.message);
            this.specificErrorMessages.set(errors);
            this.alertService.showAlert(AlertTypeEnum.ERROR, 'Login failed');
            this.loggedIn.set(false);
          } else {
            const user = response.data?.user;
            if (!user?.is_email_verified) {
              const otpData = {
                email: user.email,
                action: 'register',
              }
              this.otpEmail.set(otpData);
              this.resendCode(otpData);
              await this.router.navigate(['/auth/otp']);
              this.alertService.showAlert(
                AlertTypeEnum.SUCCESS,
                response.message || 'Please verify your email to proceed.'
              );
              this.loggedIn.set(false);
              return;
            } else {
              const user = this.userAdapter.adaptFromServer(response.data.user);
              const token = response.data.token;
              // Set signals
              this.currentUser.set(user);
              this.token.set(token);
              // Persist to storage
              this.storageService.setItem('user', user);
              this.storageService.setItem('auth_token', token);
              this.alertService.showAlert(
                AlertTypeEnum.SUCCESS,
                response.message || 'Login successful'
              );
              this.loggedIn.set(true);
              this.redirectUser(user);
            }
          }
          this.loggingIn.set(false);
        },
        error: (error) => {
          this.errorMessage.set(error.error.message || 'An error occured');
          this.alertService.showAlert(AlertTypeEnum.ERROR, 'Login failed');
          this.loggingIn.set(false);
          this.loggedIn.set(false);
        },
      });
  }

  resendCode(data: any) {
    this.specificErrorMessages.set([]);
    this.errorMessage.set(null);
    return this.http
      .post<ApiResponse>(`${environment.baseUrl}/auth/resend-code`, data)
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.specificErrorMessages.set(response.data);
            this.alertService.showAlert(
              AlertTypeEnum.ERROR,
              response.message || 'Sending Otp failed'
            );
            return;
          }

          this.alertService.showAlert(
            AlertTypeEnum.SUCCESS,
            response.message || 'OTP Sent'
          );
        },
        error: (response) => {
          this.errorMessage.set(response.error.message);
          this.specificErrorMessages.set(response.error.data);
          this.alertService.showAlert(
            AlertTypeEnum.ERROR,
            response.error.message || 'Sending Otp failed'
          );
        },
      });
  }

  requestPasswordReset(email: string) {
    this.loggingIn.set(true); // Reuse loggingIn signal for loading state
    this.errorMessage.set(null);
    this.specificErrorMessages.set([]);
    this.http
      .post<ApiResponse>(`${environment.baseUrl}/auth/forgot-password`, { email })
      .subscribe({
        next: (response: any) => {
          this.loggingIn.set(false);
          if (response.success) {
            this.otpEmail.set({ email, action: 'reset' });
            this.router.navigate(['/auth/otp']);
            this.alertService.showAlert(
              AlertTypeEnum.SUCCESS,
              response.message || 'OTP sent to your email'
            );
          } else {
            this.errorMessage.set(response.message || 'Failed to send OTP');
            this.specificErrorMessages.set(response.data || []);
            this.alertService.showAlert(AlertTypeEnum.ERROR, 'Failed to send OTP');
          }
        },
        error: (err) => {
          this.loggingIn.set(false);
          this.errorMessage.set(err.error.message || 'An error occurred');
          this.alertService.showAlert(AlertTypeEnum.ERROR, 'Failed to send OTP');
        }
      });
  }

  verifyOtp(email: string, otp: string, action: string) {
    this.loggingIn.set(true);
    this.errorMessage.set(null);
    this.specificErrorMessages.set([]);
    this.http
      .post<ApiResponse>(`${environment.baseUrl}/auth/verify-otp`, { email, code: otp, action })
      .subscribe({
        next: (response: any) => {
          this.loggingIn.set(false);
          if (response.success) {
            this.alertService.showAlert(
              AlertTypeEnum.SUCCESS,
              response.message || 'OTP verified'
            );
            if (action === 'reset') {
              this.otpEmail.update(val => val ? { ...val, code: otp } : null);
              this.router.navigate(['/auth/set-password']);
            } else if (action === 'register') {
              // For registration verification, we might want to log them in or redirect to login
              this.router.navigate(['/auth/login']);
              this.alertService.showAlert(AlertTypeEnum.SUCCESS, 'Account verified. Please login.');
            }
          } else {
            this.errorMessage.set(response.message || 'Invalid OTP');
            this.alertService.showAlert(AlertTypeEnum.ERROR, 'Invalid OTP');
          }
        },
        error: (err) => {
          this.loggingIn.set(false);
          this.errorMessage.set(err.error.message || 'An error occurred');
          this.alertService.showAlert(AlertTypeEnum.ERROR, 'Verification failed');
        }
      });
  }

  resetPassword(email: string, password: string) {
    const currentOtpData = this.otpEmail();
    const code = currentOtpData?.code;

    if (!code) {
        this.alertService.showAlert(AlertTypeEnum.ERROR, 'Session expired, please try again');
        this.router.navigate(['/auth/forgot-password']);
        return;
    }

    this.loggingIn.set(true);
    this.errorMessage.set(null);
    this.specificErrorMessages.set([]);
    this.http
      .post<ApiResponse>(`${environment.baseUrl}/auth/reset-password`, { email, password, code, password_confirmation: password })
      .subscribe({
        next: (response: any) => {
          this.loggingIn.set(false);
          if (response.success) {
            this.alertService.showAlert(
              AlertTypeEnum.SUCCESS,
              response.message || 'Password reset successful'
            );
            this.otpEmail.set(null); // Clear OTP data
            this.router.navigate(['/auth/login']);
          } else {
            this.errorMessage.set(response.message || 'Failed to reset password');
            this.specificErrorMessages.set(response.data || []);
            this.alertService.showAlert(AlertTypeEnum.ERROR, 'Failed to reset password');
          }
        },
        error: (err) => {
          this.loggingIn.set(false);
          this.errorMessage.set(err.error.message || 'An error occurred');
          this.alertService.showAlert(AlertTypeEnum.ERROR, 'Failed to reset password');
        }
      });
  }

  redirectUser(user: User) {
    switch (user.accountType) {
      case AccountType.SUPER_ADMIN:
        this.router.navigate(['/super-admin']);
        break;
      case AccountType.COUNCIL:
        this.router.navigate(['/council']);
        break;
      case AccountType.CRC:
        this.router.navigate(['/crc']);
        break;
      default:
        this.router.navigate(['/']);
        break;
    }
  }

  rehydrateUser() {
    const user = this.storageService.getItem('user');
    const token = this.storageService.getItem('auth_token');

    if (user && token) {
      this.currentUser.set(user);
      this.token.set(token);
      this.loggedIn.set(true);
    }
  }

  refreshCurrentUser(): void {
    if (!this.token()) return;

    this.http.get<ApiResponse>(`${environment.baseUrl}/user`).subscribe({
      next: (response: any) => {
        if (!response.success) return;
        this.setCurrentUser(this.userAdapter.adaptFromServer(response.data));
      },
      error: (error) => {
        if (error?.status === 401 || error?.status === 403) this.logout();
      },
    });
  }

  switchContext(context: Pick<AccountContext, 'type' | 'id'>): Observable<ApiResponse> {
    return this.http
      .post<ApiResponse>(`${environment.baseUrl}/auth/switch-context`, context)
      .pipe(
        tap((response: any) => {
          if (!response.success) return;
          const user = this.userAdapter.adaptFromServer(response.data.user);
          this.token.set(response.data.token);
          this.storageService.setItem('auth_token', response.data.token);
          this.setCurrentUser(user);
          this.redirectUser(user);
        }),
      );
  }

  setCurrentUser(user: User | null) {
    this.currentUser.set(user);

    if (user) {
      this.storageService.setItem('user', user);
      this.loggedIn.set(!!this.storageService.getItem('auth_token'));
    } else {
      this.storageService.removeItem('user');
      this.loggedIn.set(false);
    }
  }

  logout() {
    this.loggingOut.set(true);
    this.storageService.removeItem('user');
    this.storageService.removeItem('auth_token');
    this.currentUser.set(null);
    this.token.set(null);
    this.loggedIn.set(false);
    this.router.navigate(['/auth/login']);
    this.loggingOut.set(false);
  }
}
